import { normalizePublicProfile } from "./accountProfile";
import { mountAccountBlockButton, appendAccountBlockRecovery, type BlockControl, type BlockRecoveryHandler } from "../../public/shared/profile/accountBlocks.js";
import type { EpicMobileApi } from "./api";

type Removable = { remove(): Promise<void> };
type Options = { registerBack?: (close: () => void) => Promise<Removable>; onRecover?: (reason: Parameters<BlockRecoveryHandler>[0], target: string) => void };
const validUsername = (value: string) => /^[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/.test(value);

/** Other-account profiles stay inside the existing app. Never switch the acting account. */
export function createAccountProfileView(api: EpicMobileApi, options: Options = {}) {
  let owner = "", actor = "", ready = false, online = true, target = "", generation = 0;
  let dialog: HTMLDialogElement | null = null, content: HTMLElement | null = null;
  let control: BlockControl | null = null, abort: AbortController | null = null;
  let backHandle: Removable | null = null, opener: HTMLElement | null = null;
  function element<K extends keyof HTMLElementTagNameMap>(tag: K, text?: string) {
    const value = document.createElement(tag); if (text !== undefined) value.textContent = text; return value;
  }
  function clearProfile() { ++generation; abort?.abort(); abort = null; control?.dispose(); control = null; content?.replaceChildren(); }
  function close() {
    clearProfile(); dialog?.close(); dialog?.remove(); dialog = null; content = null; target = "";
    const handle = backHandle; backHandle = null; void handle?.remove();
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  }
  function status(text: string, error = false) {
    if (!content) return;
    const message = element("p", text); message.setAttribute("role", error ? "alert" : "status"); content.replaceChildren(message);
  }
  async function load(username: string) {
    if (!dialog || !ready) return;
    clearProfile(); target = username.trim().replace(/^@/, "");
    if (!validUsername(target)) { status("Enter a valid EPIC account username.", true); return; }
    if (target.toLowerCase() === owner.toLowerCase()) { status("This is your active account. Return to your profile to manage it."); return; }
    if (!online) { status("Reconnect to view this profile and manage its block.", true); return; }
    const selectedOwner = owner, selectedTarget = target, id = generation;
    const request = new AbortController(); abort = request;
    status("Loading account profile…");
    const current = () => Boolean(dialog && ready && online && id === generation && owner === selectedOwner);
    const headers = { "X-EPIC-Expected-Account": selectedOwner };
    try {
      // Resolve the immutable peer first; a reused username must not change the block target.
      const peer = await api.request<any>(`/accounts/${encodeURIComponent(selectedOwner)}/blocks/resolve/${encodeURIComponent(selectedTarget)}`, { signal: request.signal, headers });
      if (!current()) return;
      if (peer?.ok !== true || peer.accountUsername?.toLowerCase() !== selectedOwner.toLowerCase()
          || peer.username?.toLowerCase() !== selectedTarget.toLowerCase() || peer.targetKind !== "epic_account"
          || typeof peer.targetRef !== "string" || !/^epic_[A-Za-z0-9_-]{8,240}$/.test(peer.targetRef)) throw new Error("The selected account could not be verified.");
      const response = await api.request<any>(`/accounts/profile/${encodeURIComponent(peer.username)}`, { signal: request.signal, headers });
      if (!current()) return;
      if (response?.ok !== true || response.profile?.accountRef !== peer.targetRef) throw new Error("This account changed while its profile was loading. Find the account again.");
      // This separate view validates against its requested peer, never the acting-account state.
      const profile = normalizePublicProfile(response.profile, peer.username);
      const header = element("header"); header.className = "account-page__identity";
      if (profile.avatarUrl) {
        const avatar = element("img"); avatar.src = profile.avatarUrl; avatar.alt = `${profile.displayName} avatar`;
        avatar.width = 80; avatar.height = 80; avatar.referrerPolicy = "no-referrer";
        avatar.style.cssText = "border-radius:50%;object-fit:cover;max-width:80px"; header.append(avatar);
      }
      const identity = element("div"), name = element("h2", profile.displayName || profile.username);
      name.setAttribute("data-mobile-peer-name", ""); identity.append(name, element("p", `@${profile.username}`));
      header.append(identity);
      const actions = element("section"); actions.setAttribute("aria-label", "Account interaction controls");
      content!.replaceChildren(header, actions, element("p", profile.bio || "No bio yet."));
      dialog!.setAttribute("aria-label", `Profile of @${profile.username}`);
      control = mountAccountBlockButton({ root: actions, accountUsername: selectedOwner, targetRef: peer.targetRef,
        request: (path, init) => api.request(path, init), onRecover: reason => { close(); options.onRecover?.(reason, selectedTarget); } });
      dialog!.querySelector<HTMLButtonElement>("[data-mobile-peer-back]")?.focus({ preventScroll: true });
    } catch (error: any) {
      if (current()) {
        status(error?.message || "The profile could not be loaded. Try again.", true);
        appendAccountBlockRecovery({ root: content!, error, onRetry: () => void load(selectedTarget),
          onRecover: reason => { close(); options.onRecover?.(reason, selectedTarget); } });
      }
    }
  }
  function open(username = "") {
    if (!ready || !owner) return;
    if (!dialog) {
      opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog = element("dialog"); dialog.setAttribute("aria-label", "Find an EPIC account");
      dialog.setAttribute("data-mobile-peer-profile", "");
      dialog.style.cssText = "box-sizing:border-box;width:min(100vw,48rem);max-width:100vw;height:100dvh;max-height:100dvh;margin:auto;padding:calc(16px + env(safe-area-inset-top)) 20px calc(16px + env(safe-area-inset-bottom));border:0;overflow:auto;color:var(--ink,#17191e);background:var(--surface,#fff);";
      const theme = document.querySelector<HTMLElement>("[data-mobile-root]");
      if (theme) for (const key of ["--ink", "--surface"]) dialog.style.setProperty(key, getComputedStyle(theme).getPropertyValue(key));
      const back = element("button", "Back to my profile"); back.type = "button"; back.style.minHeight = "44px";
      back.setAttribute("data-mobile-peer-back", ""); back.onclick = close;
      const title = element("h1", "User profile"), form = element("form"), label = element("label", "EPIC account username ");
      const input = element("input"); input.name = "username"; input.maxLength = 120; input.required = true; input.autocomplete = "off";
      input.placeholder = "username"; input.setAttribute("autocapitalize", "none"); input.spellcheck = false;
      input.style.cssText = "box-sizing:border-box;min-height:44px;max-width:100%";
      const find = element("button", "View profile"); find.type = "submit"; find.style.minHeight = "44px";
      label.append(input); form.append(label, find); form.onsubmit = event => { event.preventDefault(); void load(input.value); };
      content = element("section"); content.setAttribute("aria-label", "Selected user profile");
      dialog.append(back, title, form, content); document.body.append(dialog);
      dialog.addEventListener("cancel", event => { event.preventDefault(); close(); });
      dialog.showModal(); input.focus();
      if (options.registerBack) {
        const opened = dialog;
        void options.registerBack(close).then(handle => {
          if (dialog !== opened) void handle.remove(); else backHandle = handle;
        }).catch(() => { /* The visible Back button remains available. */ });
      }
    }
    if (username) { dialog.querySelector<HTMLInputElement>("input")!.value = username; void load(username); }
  }
  return { open, close, isOpen: () => Boolean(dialog),
    refresh() { if (dialog && target && ready && online) void load(target); },
    sync(account: string, user: string, authenticated: boolean, connected = true) {
      if (account !== owner || user !== actor || !authenticated) close();
      const reconnected = !online && connected;
      owner = account; actor = user; ready = authenticated; online = connected;
      if (dialog && !online) { clearProfile(); status("Reconnect to view this profile and manage its block.", true); }
      else if (dialog && reconnected && target) void load(target);
    }
  };
}
